<script setup>
    import { useSitesStore } from '@/stores/sites';
    import { useNeighborhoodsStore } from '@/stores/neighborhoods';
    import { usePointsOfInterestStore } from '@/stores/points-of-interest';
    import { useToursStore } from '@/stores/tours';
    import { updateHead } from '@/composables/helpers/updateHead';
    import { neighborhoodSnakeCase } from '~/composables/helpers/neighborhoodSnakeCase.js';
    import { useFooterCta } from '@/composables/useFooterCta';
    import { detailTitle, seoDescription, shareImage } from '@/utils/seo';

    const route = useRoute();

    const { sitesData } = storeToRefs(useSitesStore());
    const allSites = computed(() => sitesData.value);

    const { neighborhoodsData } = storeToRefs(useNeighborhoodsStore());
    const neighborhoods = computed(() => neighborhoodsData.value);

    const { pointsOfInterestData } = storeToRefs(usePointsOfInterestStore());
    const pointsOfInterest = computed(() => pointsOfInterestData.value);

    const { toursData } = storeToRefs(useToursStore());
    const tours = computed(() => toursData.value);

    const pathId = route.params.neighborhood[0];

    const neighborhoodData = neighborhoods.value.find((neighborhood) => neighborhood.id === pathId);

    // A real 404 for an id that isn't a neighborhood, instead of a 200 page
    // reading "Missing Name". Only when the list actually loaded, so a failed
    // bootstrap can't 404 every neighborhood.
    if (import.meta.server && neighborhoods.value?.length && !neighborhoodData) {
        throw createError({ statusCode: 404, statusMessage: 'Neighborhood not found', fatal: true });
    }

    const moveToFront = (data, matchingId) => {
        const index = data.findIndex(({ id }) => id === matchingId);
        if (index !== -1) {
            const updatedData = [...data];
            updatedData.unshift(...updatedData.splice(index, 1));
            return updatedData;
        } else {
            return data.unshift(foldedMapProject.value);
        }
    };
    const pointsOfInterestByNeighborhood = computed(() => {
        let points = [];
        if (pointsOfInterest.value && pointsOfInterest.value.length > 0) {
            points = pointsOfInterest.value.filter((item) => {
                if (item.fields?.Neighborhoods) {
                    return item.fields?.Neighborhoods.find((id) => id === pathId);
                }
            });
            if (foldedMapProject.value) {
                moveToFront(points, 'recfRLBk6a771OjQb');
            }
        }
        return points;
    });

    const foldedMapProject = computed(() => {
        if (pointsOfInterest.value && pointsOfInterest.value?.length > 0) {
            return pointsOfInterest.value.find((item) => item.id === 'recfRLBk6a771OjQb');
        }
        return false;
    });

    const nearbyNeighborhoods = computed(() => {
        const nearbyNeighborhoodIds = neighborhoodData?.fields['Nearby Neighborhoods'] ? neighborhoodData?.fields['Nearby Neighborhoods'] : null;
        const nearbyNeighborhoodsArr = [];
        if (nearbyNeighborhoodIds) {
            nearbyNeighborhoodIds.forEach((id) => {
                const neighborhood = neighborhoods.value.find((neighborhood) => neighborhood.id === id);
                // An id that resolves to nothing used to be pushed anyway, which
                // rendered a card with no name, no artwork and a link to
                // /neighborhoods/undefined. Near West Side has one: the
                // Neighborhoods collection is filtered to
                // "Final and Ready for Publication", so a linked neighborhood
                // that has not reached that state simply is not in the list.
                if (neighborhood) nearbyNeighborhoodsArr.push(neighborhood);
            });
        }
        return nearbyNeighborhoodsArr;
    });

    // Matched through the neighborhood's SITES rather than the itinerary's own
    // `OHC Neighborhood` link (Asana O2-60). That field is set on 2 of 16
    // itineraries, and this compared only its FIRST entry, so the block almost
    // never rendered — Near West Side has six itineraries running through it and
    // showed none. See itinerariesForNeighborhood for why the two sides need
    // resolving before they can be compared at all.
    const ohcTours = computed(() =>
        itinerariesForNeighborhood(neighborhoodData, sitesData.value, tours.value?.records || []));

    const mapView = ref(false);

    // See Sites/single.vue — the CTA now lives in the global footer block.
    // Held back in map view: that view fills the viewport, so the CTA sat on
    // the screen edge and scrolling pulled it up over the map and the site
    // list (Asana O2-74). Declared here rather than at the top of setup
    // because it reads mapView.
    useFooterCta(() => !mapView.value);

    const coverImages = computed(() => {
        return neighborhoodData?.fields?.['Cover Image'] || [];
    });

    const name = computed(() => {
        const name = neighborhoodData?.fields?.Name || 'Missing Name';
        return name;
    });

    const desc = computed(() => {
        const desc = neighborhoodData?.fields?.Description || 'Missing Description';
        return desc;
    });

    // Shared with the neighborhood card's "N Sites" count so the two can never
    // disagree — see useNeighborhoodSites.
    const cardsData = computed(() => neighborhoodSiteRecords(neighborhoodData, allSites.value));

    // Description was `slice(0, 150)`, which stopped mid-word; now cut at a
    // word boundary. The share image is the neighborhood's cover photo.
    const shareCover = shareImage(coverImages.value, neighborhoodData?.fields?.Name, 0);
    updateHead({
        title: detailTitle(neighborhoodData?.fields?.Name),
        description: seoDescription(neighborhoodData?.fields?.Description),
        path: route.path,
        image: shareCover.url,
        imageWidth: shareCover.width,
        imageHeight: shareCover.height,
        imageAlt: shareCover.alt,
    });
</script>

<template>
    <div class="neighborhood-detail">
        <button class="map-toggle top-actions" :class="mapView ? 'map-view' : ''" @click="mapView = !mapView">
            <SvgsMapWhiteCircle v-show="!mapView" />
            <SvgsListWhiteCircle v-show="mapView" />
        </button>
        <BlocksTitleWithMapAndCards :title="name" :cards="cardsData" v-if="mapView" />        
        <section v-show="!mapView">
            <!-- hd-container, like the block below it: that is what carries the
                 16px gutter under 1182px. Without it the map and description ran
                 flush to the window edges while everything beneath them was
                 inset, and the description clipped at the right. -->
            <div class="image-description-wrapper container hd-container">
                <BlocksNeighborhoodImage :name="neighborhoodSnakeCase(name)" :images="coverImages" />
                <div class="wrapper">
                    <BlocksNeighborhoodName :detail="name" />
                    <BlocksDetailDescription>
                        <div v-if="desc" v-html="desc" class="description"></div>
                    </BlocksDetailDescription>
                </div>
            </div>
            <div class="hd-container">
                <!-- Sites first, itineraries second — Asana O2-75. "Sites need to
                     be the main focus when people are looking at the neighborhood
                     pages." Itineraries led the page and pushed the sites below
                     the fold on a laptop.

                     A straight swap of two siblings: the vertical rhythm below
                     gives every block in that selector list the same top padding
                     with bottom zeroed, so order carries no spacing of its own.
                     Sites has no v-if, so it is always the first block here now;
                     tours-list already disappeared when a neighborhood had no
                     itineraries. -->
                <BlocksSitesListStack :showHeader="true" :cards="cardsData" :componentLocation="'neighborhood'" />
                <BlocksToursList v-if="ohcTours?.length > 0" :items="ohcTours" title="Itineraries in this Area" :showViewAll="false" />
                <BlocksNeighborhoodEvents :neighborhoodId="pathId" />
                <BlocksLikeALocalCarousel v-if="pointsOfInterestByNeighborhood?.length > 0" :recommendations="pointsOfInterestByNeighborhood" />
                <BlocksNearbyNeighborhoods v-if="nearbyNeighborhoods?.length > 0" :neighborhoods="nearbyNeighborhoods" />
            </div>
        </section>
    </div>
</template>

<style lang="scss">
    .neighborhood-detail {

        // Equal vertical rhythm between the stacked blocks. Measured before
        // this, the gaps ran 32 / 20 / 30 / 60 / 60: every block carried its own
        // ad-hoc top and bottom padding, and where two adjacent blocks both had
        // some, the gap was their sum.
        //
        // One value, owned by the TOP edge of each block, with bottom padding
        // zeroed — so a gap can never again be the sum of two contributions.
        // Change $section-rhythm and the whole column moves together.
        //
        // Anchored three classes deep on purpose. sites-list-stack,
        // neighborhood-events and nearby-neighborhoods all style themselves in
        // SCOPED blocks, and `.foo[data-v-x]` is (0,2,0); this is (0,3,0), so it
        // wins regardless of which stylesheet the bundler emits first. A
        // two-class selector would tie and the result would depend on build
        // order — the dev/prod divergence trap.
        //
        // Scoped to this page because tours-list and sites-list-stack are shared
        // with the city-tours, youth-programs, archive and Ghost variants.
        $section-rhythm: 60px;

        .hd-container > .tours-list,
        .hd-container > .sites-list-stack,
        .hd-container > .neighborhood-events,
        .hd-container > .like-a-local-carousel,
        .hd-container > .nearby-neighborhoods {
            padding-top: $section-rhythm;
            padding-bottom: 0;
        }

        position: relative;

        @media (min-width: $tablet) {
            max-width: $hd;
            width: 100vw;
            margin: auto;
            min-height: 60vh;
            padding-top: 20px;
        }

        .map-toggle {
            position: absolute;
            top: 16px;
            right: 16px;

            z-index: 57;
            width: 44px;
            height: 44px;

            display: flex;
            align-items: center;
            justify-content: center;

            @media (min-width: $tablet) {
                // On the neighbourhood name's line rather than its own row above
                // it. The name starts at the article's padding-top and its line
                // is 32px; the button is 44px, so pulling it up by half the
                // difference centres the two on each other.
                top: calc(20px - 6px);
                // Level with the content's right edge, not the window's. Below
                // $hd + 32px the content carries hd-container's 16px gutter, so
                // the button has to sit inside it too or it hangs out past
                // everything else on the page.
                right: 16px;
            }

            // hd-container drops its gutter once the cap provides one, so the
            // button follows it back out to the content edge.
            @media (min-width: $hd + 32px) {
                right: 0;
            }
        }

        .neighborhood-name h1 {
            // The toggle now shares this line, and it is absolutely positioned
            // over the title's own column — so nothing stops a long name
            // running underneath it. "Garfield Park / North Lawndale", the
            // longest in the collection, cleared the button by 9px at 768.
            // Reserving the button's 44px plus a gap makes the name wrap before
            // it reaches the toggle instead of behind it. Short names are
            // unaffected: padding only moves where the text breaks.
            @media (min-width: $tablet) {
                padding-right: 52px;
            }
        }

        #title-map-header {
            // Only the vertical padding belongs to this page. The horizontal
            // gutter is hd-container's job, and the header already carries that
            // class — but an id beats a class, so the shorthand here was
            // overriding it in both directions. Above $tablet that zeroed the
            // gutter outright and the title sat hard against the window edge
            // while everything else on the page was inset.
            padding-top: 59px;
            padding-bottom: 0;

            @media (min-width: $tablet) {
                padding-top: 0;
            }
        }

        .wrapper {
            // The 16px that used to live here was standing in for a gutter the
            // hero wrapper did not have. It has one now (hd-container), so
            // keeping this indented the title and description 32px on mobile
            // while the map beside them and every heading below sat at 16.
            padding: 0;
        }

        .image-description-wrapper.container {
            // No `padding: 0` here any more. It was cancelling the padding
            // .container used to carry — that rule is commented out now, so the
            // only thing it still cancelled was the hd-container gutter this
            // element needs, and it won because two classes plus the scope
            // attribute outrank a single .hd-container.
            margin-bottom: 0;

            // Was 32px at $tablet+. Dropped so the run-in to the first block
            // below is the block's own $section-rhythm and nothing else —
            // otherwise that one gap is 32 + 60 = 92 while every other pair is
            // 60. Mobile was already 0, so this only levels the desktop case.
            @media (min-width: $tablet) {
                margin-bottom: 0;
            }
        }

        @media (min-width: $tablet) {

            .image-description-wrapper.container {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                // Row gap stays at 16 — this is a single row, so only the
                // column gap between the map and the copy beside it is doing
                // any work, and 16 ran them too close together.
                gap: 16px 20px;
            }
            
            .neighborhood-hero-image figure {
                height: 100%;
                max-height: 500px;
            }
        }

        .description p {
            margin-top: 20px;
        }
    }
</style>
